import { gql, useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import type {
  PostTopicIdsQuery,
  PostTopicIdsQueryVariables,
} from '../graphql/generated/graphqlTypes';
import updateSubscribedList from '../utils/graphql/updateSubscribedList';
import useSlug from '../utils/router/useSlug';
import useIsPublicView from '../utils/useIsPublicView';

const POST_TOPIC_IDS_QUERY = gql`
  query postTopicIds($id: ID!) {
    post(id: $id) {
      id
      topics {
        id
      }
    }
  }
`;

const POST_TOPIC_ID_ADDED_SUBSCRIPTION = gql`
  subscription postTopicIdAdded($postId: ID!) {
    topic: postTopicAdded(postId: $postId) {
      id
    }
  }
`;

const POST_TOPIC_ID_REMOVED_SUBSCRIPTION = gql`
  subscription postTopicIdRemoved($postId: ID!) {
    topic: postTopicRemoved(postId: $postId) {
      id
    }
  }
`;

const useCurrentPostTopicIds = (
  opts: {
    subscribeToMore?: boolean;
  } = {},
) => {
  const [postId] = useSlug('postSlug');
  const isPublicView = useIsPublicView();
  const { data, subscribeToMore } = useQuery<
    PostTopicIdsQuery,
    PostTopicIdsQueryVariables
  >(POST_TOPIC_IDS_QUERY, {
    variables: { id: postId || '' },
    skip: !postId || isPublicView,
  });

  useEffect(() => {
    if (!postId || !opts.subscribeToMore) {
      return undefined;
    }
    const unsubscribes = [
      POST_TOPIC_ID_ADDED_SUBSCRIPTION,
      POST_TOPIC_ID_REMOVED_SUBSCRIPTION,
    ].map((query) =>
      subscribeToMore({
        document: query,
        variables: { postId },
        updateQuery: updateSubscribedList(
          'post.topics',
          'topic',
          query === POST_TOPIC_ID_ADDED_SUBSCRIPTION,
        ),
      }),
    );

    return () => {
      unsubscribes.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [postId, subscribeToMore, opts.subscribeToMore]);

  return useMemo(() => data?.post.topics.map(({ id }) => id) ?? [], [data]);
};

export default useCurrentPostTopicIds;
